/**
 * M5T VCR Controls Web Component
 * Transport controls for the PubSub tape recorder (shared/pubsubVCR.js)
 *
 * Instantiated from template markup rather than placed by hand - a template
 * returns `<m5t-vcr-controls></m5t-vcr-controls>` and the parser upgrades it when
 * <m5t-chat-workflow> installs the markup. Nothing else wires it up.
 *
 * Talks to the recorder through its `window` hooks (startVCR, playVCR, ...) and
 * not through PubSub, deliberately: `shared/` cannot resolve a bare 'pubsub-js'
 * specifier, and more importantly a control surface that published its own
 * commands would land those commands on the tape it is recording.
 *
 * REPLAY IS NOT INERT
 *
 * Replayed events reach real subscribers - a tape holding AUTH.SIGNOUT.GO signs
 * you out, LLM.PROMPT calls the model. This surface therefore defaults "skip
 * AUTH/LLM" ON, so a first click of Play in a live authenticated session cannot
 * end it. Uncheck for a full-fidelity replay once you know what is on the tape.
 */

const POLL_MS = 500;

/** Prefixes whose replay has real consequences - mirrors HIGH_IMPACT in pubsubVCR.js. */
const HIGH_IMPACT = /^(AUTH|ROUTE|LLM)\./;

const STYLES = `
  :host { display: block; font-size: 0.9rem; }

  .panel { display: flex; flex-direction: column; gap: 0.75rem; }

  .status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
    color: #a8a8a8;
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #3a3a3a;
    flex: none;
  }

  .dot[data-state="recording"] { background: #DD4124; animation: pulse 1.2s ease-in-out infinite; }
  .dot[data-state="playing"]   { background: #009473; animation: pulse 1.2s ease-in-out infinite; }
  .dot[data-state="loaded"]    { background: #edba08; }

  @keyframes pulse { 50% { opacity: 0.25; } }

  .count { color: #e4e4e4; font-variant-numeric: tabular-nums; }

  .row { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }

  button {
    background: #2d2d2d;
    color: #e4e4e4;
    border: 1px solid #3a3a3a;
    border-radius: 4px;
    padding: 0.45rem 0.9rem;
    font: inherit;
    font-size: 0.8rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  button:hover:not(:disabled) {
    background: #1a1a1a;
    border-color: #edba08;
    transform: translateY(-1px);
  }

  button:disabled { opacity: 0.35; cursor: not-allowed; }

  button:focus-visible { outline: 2px solid #edba08; outline-offset: 2px; }

  button[data-variant="record"]:not(:disabled) { border-color: #810000; color: #DD4124; }
  button[data-variant="play"]:not(:disabled)   { border-color: #003817; color: #009473; }

  .options {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: center;
    font-size: 0.75rem;
    color: #a8a8a8;
    padding-top: 0.25rem;
    border-top: 1px solid #2d2d2d;
  }

  label { display: flex; align-items: center; gap: 0.35rem; cursor: pointer; }

  select {
    background: #2d2d2d;
    color: #e4e4e4;
    border: 1px solid #3a3a3a;
    border-radius: 3px;
    padding: 0.2rem 0.35rem;
    font: inherit;
    font-size: 0.75rem;
  }

  .warn { color: #F5DF4D; }
  .err  { color: #DD4124; }
`;

class M5TVcrControls extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._playing = false;
    this._note = '';
    this._timer = null;
  }

  connectedCallback() {
    this.build();
    this.sync();
    // The recorder can also be driven from the console, so this surface polls
    // rather than assuming it is the only thing touching the tape.
    this._timer = setInterval(() => this.sync(), POLL_MS);
  }

  disconnectedCallback() {
    clearInterval(this._timer);
    this._timer = null;
  }

  get installed() {
    return typeof window.startVCR === 'function';
  }

  get recording() {
    return !!window._isVCRRecording;
  }

  get tape() {
    return window._vcrTimeline ?? [];
  }

  build() {
    if (!this.installed) {
      this.shadowRoot.innerHTML = `
        <style>${STYLES}</style>
        <p class="err">
          The tape recorder is not in this build - no <code>installPubSubVCR()</code> call ran.
          Check the console for a <code>[VCR] armed</code> line.
        </p>
      `;
      return;
    }

    this.shadowRoot.innerHTML = `
      <style>${STYLES}</style>
      <div class="panel">
        <div class="status">
          <span class="dot" data-state="idle"></span>
          <span class="label">Idle</span>
          <span class="count"></span>
        </div>

        <div class="row">
          <button data-act="record" data-variant="record">● Record</button>
          <button data-act="stop">■ Stop</button>
          <button data-act="save">⤓ Save</button>
          <button data-act="load">⤒ Load</button>
          <button data-act="play" data-variant="play">▶ Play</button>
          <button data-act="cancel">⏹ Cancel</button>
        </div>

        <div class="options">
          <label>
            Speed
            <select data-opt="speed">
              <option value="1">1×</option>
              <option value="4">4×</option>
              <option value="0">instant</option>
            </select>
          </label>
          <label><input type="checkbox" data-opt="dryRun"> Dry run</label>
          <label><input type="checkbox" data-opt="skip" checked> Skip AUTH/LLM</label>
          <span class="note"></span>
        </div>
      </div>
    `;

    // One delegated listener; no per-button handlers to lose on re-render.
    this.shadowRoot.addEventListener('click', this.onClick);
  }

  onClick = (e) => {
    const act = e.target?.closest?.('button')?.dataset?.act;
    if (act) this.run(act);
  };

  opt(name) {
    const el = this.shadowRoot.querySelector(`[data-opt="${name}"]`);
    if (!el) return null;
    return el.type === 'checkbox' ? el.checked : el.value;
  }

  async run(act) {
    try {
      switch (act) {
        case 'record':
          window.startVCR();
          this._note = '';
          break;

        case 'stop':
          window.stopVCR({ save: false });
          this._note = `stopped - ${this.tape.length} event(s) on tape`;
          break;

        case 'save':
          window.saveVCR();
          this._note = 'written to Downloads';
          break;

        case 'load': {
          // Async: opens a file picker. Resolves to the entry count.
          const count = await window.loadVCR();
          this._note = `loaded ${count ?? this.tape.length} event(s)`;
          break;
        }

        case 'play': {
          if (this._playing) return;
          this._playing = true;
          this._note = '';
          this.sync();

          const result = await window.playVCR({
            speed: Number(this.opt('speed')),
            dryRun: this.opt('dryRun'),
            skip: this.opt('skip') ? ['AUTH', 'LLM'] : []
          });

          this._playing = false;
          this._note = result.cancelled
            ? `cancelled after ${result.played}`
            : `played ${result.played}, skipped ${result.skipped} in ${result.durationMs}ms`;
          break;
        }

        case 'cancel':
          window.stopPlayback();
          this._playing = false;
          break;
      }
    } catch (err) {
      console.error('[M5TVcrControls] action failed', act, err);
      this._playing = false;
      this._note = err.message;
    }

    this.sync();
  }

  /** Reflect recorder state into the UI. Cheap enough to run on a poll. */
  sync() {
    if (!this.installed) return;

    const { length } = this.tape;
    const recording = this.recording;
    const playing = this._playing;

    const state = recording ? 'recording' : playing ? 'playing' : length ? 'loaded' : 'idle';
    const labels = {
      recording: 'Recording',
      playing: 'Replaying',
      loaded: 'Tape ready',
      idle: 'Idle'
    };

    this.shadowRoot.querySelector('.dot').dataset.state = state;
    this.shadowRoot.querySelector('.label').textContent = labels[state];
    this.shadowRoot.querySelector('.count').textContent = length ? `${length} event(s)` : '';

    const disabled = {
      record: recording || playing,
      stop: !recording,
      save: !length || recording,
      load: recording || playing,
      play: !length || recording || playing,
      cancel: !playing
    };

    for (const [act, off] of Object.entries(disabled)) {
      this.shadowRoot.querySelector(`[data-act="${act}"]`).disabled = off;
    }

    // Surface the side-effect risk before Play is pressed, not after.
    const risky = this.tape.filter((e) => HIGH_IMPACT.test(e.message)).length;
    const note = this.shadowRoot.querySelector('.note');

    if (this._note) {
      note.className = 'note';
      note.textContent = this._note;
    } else if (risky && !this.opt('skip') && !this.opt('dryRun')) {
      note.className = 'note warn';
      note.textContent = `⚠ ${risky} AUTH/ROUTE/LLM event(s) will really fire`;
    } else {
      note.className = 'note';
      note.textContent = '';
    }
  }
}

customElements.define('m5t-vcr-controls', M5TVcrControls);

export default M5TVcrControls;
